<template>
  <div class="w-full h-full overflow-y-scroll px-5">
    <div class="mx-auto px-4 sm:px-8 xl:px-0 mt-6 flex gap-6">
      <aside class="w-1/5 hidden lg:block">
        <h3 class="text-lg font-semibold mb-4">Категории</h3>
        <ul class="space-y-2">
          <li
            v-for="cat in categories"
            :key="cat.id"
            @click="selectCategory(cat.id)"
            class="cursor-pointer text-sm px-3 py-2 rounded hover:bg-gray-100"
            :class="{ 'bg-gray-200 font-medium': selectedCategory === cat.id }"
          >
            {{ cat.name }}
          </li>
          <li
            @click="selectCategory(null)"
            class="cursor-pointer text-sm px-3 py-2 rounded hover:bg-gray-100"
            :class="{ 'bg-gray-200 font-medium': selectedCategory === null }"
          >
            Все категории
          </li>
        </ul>
      </aside>

      <section class="flex-1">
        <div class="mb-8 flex justify-between items-center">
          <div class="flex-1 text-center">
            <h1 class="text-2xl sm:text-3xl font-bold">Каталог продукции БПЛА</h1>
          </div>
          <button
            v-if="isAuthenticated && (user.role && user.role.key !== 1)"
            @click="showSuggestForm = true"
            class="px-4 md:px-6 py-2 bg-gray-800 text-white rounded-xl hover:bg-gray-700 transition font-medium text-xs md:text-sm whitespace-nowrap ml-4"
          >
            <b>Предложить продукт</b>
          </button>
        </div>

        <!-- Filters and Search -->
        <div class="mb-6 bg-gray-50 p-4 rounded-lg">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Поиск</label>
              <input
                v-model="searchQuery"
                @input="debouncedFetch"
                type="text"
                placeholder="Название товара..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Производитель</label>
              <input
                v-model="filters.manufacturer"
                @input="debouncedFetch"
                type="text"
                placeholder="Производитель..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Назначение</label>
              <input
                v-model="filters.purpose"
                @input="debouncedFetch"
                type="text"
                placeholder="Назначение..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Поставщик</label>
              <input
                v-model="filters.supplier"
                @input="debouncedFetch"
                type="text"
                placeholder="Поставщик..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Действия</label>
              <button
                @click="resetFilters"
                class="w-full px-3 py-2 bg-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-400 transition"
              >
                Очистить
              </button>
            </div>
          </div>
        </div>

        <div v-if="productList === null" class="text-center text-gray-500">
          Загрузка...
        </div>
        <div
          v-else-if="productList.length === 0"
          class="text-center text-gray-500"
        >
          Нет данных.
        </div>

        <div
          v-else
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6"
        >
          <div
            v-for="item in productList"
            :key="item.slug"
            class="p-4 border rounded-md shadow-sm cursor-pointer hover:shadow-lg transition"
            @click="
              $router.push({
                name: 'PageContentDetail',
                params: { contentType: contentType, slug: item.slug },
              })
            "
          >
            <div class="mb-4">
              <img
                :src="item.image || '/images/placeholder.png'"
                alt="Изображение"
                class="w-full h-48 object-contain rounded"
              />
            </div>
            <h3 class="text-lg font-semibold mb-2">{{ item.title }}</h3>
            <p v-html="item.content" class="text-gray-700 line-clamp-3 mb-3"></p>
            <div v-if="item.uav_catalog" class="bg-blue-50 p-3 rounded text-sm space-y-1">
              <div v-if="item.uav_catalog.manufacturer" class="flex justify-between">
                <span class="font-medium text-gray-600">Производитель:</span>
                <span class="text-gray-900">{{ item.uav_catalog.manufacturer }}</span>
              </div>
              <div v-if="item.uav_catalog.purpose" class="flex justify-between">
                <span class="font-medium text-gray-600">Назначение:</span>
                <span class="text-gray-900">{{ item.uav_catalog.purpose }}</span>
              </div>
              <div v-if="item.uav_catalog.cost" class="flex justify-between">
                <span class="font-medium text-gray-600">Стоимость:</span>
                <span class="text-gray-900 font-semibold">{{ item.uav_catalog.cost }}</span>
              </div>
              <div v-if="item.uav_catalog.supplier" class="flex justify-between">
                <span class="font-medium text-gray-600">Поставщик:</span>
                <span class="text-gray-900">{{ item.uav_catalog.supplier }}</span>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="totalPages > 1"
          class="flex justify-center items-center mt-10 gap-2 flex-wrap"
        >
          <label class="mr-2 text-sm font-medium">Страница:</label>
          <select
            v-model.number="currentPage"
            @change="handlePageChange"
            class="border px-3 py-2 rounded-md w-20"
          >
            <option v-for="n in totalPages" :key="n" :value="n">
              {{ n }}
            </option>
          </select>
          <span class="text-sm text-gray-600">из {{ totalPages }}</span>
        </div>
      </section>
    </div>

    <!-- Modal: Suggest Product -->
    <div v-if="showSuggestForm" class="fixed inset-0 bg-black bg-opacity-50 z-[10010] flex items-center justify-center p-4">
      <div class="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div class="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <h2 class="text-2xl font-bold text-gray-900">
            Предложить продукт
          </h2>
          <button
            @click="showSuggestForm = false"
            class="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ✕
          </button>
        </div>

        <div class="p-6">
          <ContentSuggestForm
            content-type="uav_catalog"
            @submit="handleSuggestSubmit"
            @close="showSuggestForm = false"
          />
        </div>
      </div>
    </div>

    <div class="pb-10"></div>
  </div>
</template>

<script>
import axios from "axios";
import { API_CONTENT, API_CATEGORIES_CLIENT } from "@/const/uri";
import ContentSuggestForm from "@/components/PageContent/ContentSuggestForm.vue";
import { mapState } from 'vuex';
import { debounce } from '@/utils/debounce';

export default {
  name: "PageProducts",
  components: {
    ContentSuggestForm,
  },
  data() {
    return {
      productList: null,
      categories: [],
      selectedCategory: null,
      currentPage: 1,
      perPage: 6,
      totalPages: 1,
      showSuggestForm: false,
      isAuthenticated: false,
      searchQuery: '',
      filters: {
        manufacturer: '',
        purpose: '',
        supplier: ''
      }
    };
  },
  computed: {
    ...mapState(["user"]),
    contentType() {
      return this.$route.params.contentType;
    },
    queryPage() {
      const p = parseInt(this.$route.query.p);
      return isNaN(p) || p < 1 ? 1 : p;
    },
  },
  watch: {
    "$route.query.p": {
      immediate: true,
      handler() {
        this.currentPage = this.queryPage;
        this.fetchProducts();
      },
    },
  },
  created() {
    this.debouncedFetch = debounce(() => {
      this.currentPage = 1;
      this.fetchProducts();
    }, 400);
  },
  mounted() {
    this.checkAuthentication();
    this.fetchCategories();
  },
  beforeUnmount() {
    if (this.debouncedFetch) this.debouncedFetch.cancel();
  },
  methods: {
    checkAuthentication() {
      const token = localStorage.getItem('user_token');
      this.isAuthenticated = !!token;
    },
    handleSuggestSubmit() {
      this.showSuggestForm = false;
      this.fetchProducts();
    },
    async fetchProducts() {
      try {
        const params = {
          content_type: this.contentType,
          category: this.selectedCategory || undefined,
          p: this.currentPage,
          page_size: this.perPage,
        };

        // Add search query
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }

        // Add filters
        if (this.filters.manufacturer) {
          params.manufacturer = this.filters.manufacturer;
        }
        if (this.filters.purpose) {
          params.purpose = this.filters.purpose;
        }
        if (this.filters.supplier) {
          params.supplier = this.filters.supplier;
        }

        const response = await axios.get(API_CONTENT.list, { params });

        if (response.data.code === 0) {
          const data = response.data.data;
          this.productList = data.results;
          this.totalPages = Math.ceil(data.count / this.perPage);
        }
      } catch (error) {
        console.error("Ошибка при загрузке продукции:", error);
      }
    },
    async fetchCategories() {
      try {
        const res = await axios.get(
          API_CATEGORIES_CLIENT.base,
          {
            params: {
              content_type: this.$route.params.contentType,
            },
          },
        );
        if (res.data.code === 0) {
          this.categories = res.data.data;
        }
      } catch (error) {
        console.error("Ошибка загрузки категорий:", error);
      }
    },
    selectCategory(categoryId) {
      this.selectedCategory = categoryId;
      this.currentPage = 1;
      this.updateRoutePage(1);
      this.fetchProducts();
    },
    handlePageChange() {
      this.updateRoutePage(this.currentPage);
    },
    updateRoutePage(page) {
      this.$router.push({
        name: "PageContent",
        params: {
          contentType: this.contentType,
        },
        query: {
          ...this.$route.query,
          p: page,
        },
      });
    },
    resetFilters() {
      this.searchQuery = '';
      this.filters = {
        manufacturer: '',
        purpose: '',
        supplier: ''
      };
      this.currentPage = 1;
      this.fetchProducts();
    },
  },
};
</script>
